import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api";
import type { CrmCatalog, LeadFilters, LeadStats, LeadView, Manager } from "../types";
import { LeadEditModal } from "../components/LeadEditModal";
import { fmtDateParts } from "../components/util";
import { TEMPERATURE, labelFor } from "../components/LeadCard";

const EMPTY_STATS: LeadStats = {
  total: 0,
  by_temperature: {},
  by_status: {},
  by_category: {},
  by_interest: {},
  by_city: [],
  events: [],
  follow_ups_due: 0,
};

/** Общая таблица лидов: всё, что привезли с выставок, с фильтрами и выгрузкой. */
export function LeadsPage() {
  const [leads, setLeads] = useState<LeadView[]>([]);
  const [stats, setStats] = useState<LeadStats>(EMPTY_STATS);
  const [catalog, setCatalog] = useState<CrmCatalog | null>(null);
  const [params] = useSearchParams();
  const [filters, setFilters] = useState<LeadFilters>(() =>
    params.get("stale") === "1" ? { stale: "1" } : {},
  );
  const [managers, setManagers] = useState<Manager[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<LeadView | null>(null);
  /** Сколько ещё нашлось в архиве по строке поиска — там их не показываем */
  const [archivedMatches, setArchivedMatches] = useState(0);

  useEffect(() => {
    api.leadCatalog().then(setCatalog).catch(() => setCatalog(null));
    api.managers().then(setManagers).catch(() => setManagers([]));
  }, []);

  const load = useCallback(() => {
    api
      .listLeads(filters)
      .then((r) => {
        setLeads(r.leads);
        setStats(r.stats);
        setArchivedMatches(r.archived_matches ?? 0);
        setError("");
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(load, filters.search ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, filters.search]);

  const set = (key: keyof LeadFilters, value: string) =>
    setFilters((f) => ({ ...f, [key]: value || undefined }));

  const active = Object.entries(filters).filter(
    ([, v]) => v !== undefined && String(v) !== "",
  ).length;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Лиды</h1>
          <p>
            Клиенты со встреч. Карточка собирается из записи разговора
            автоматически, любое поле можно поправить руками.
          </p>
        </div>
        <div className="row">
          {active > 0 && (
            <button onClick={() => setFilters({})}>Сбросить фильтры</button>
          )}
          <a className="btn btn-primary" href={api.leadsExportUrl(filters)}>
            Выгрузить в Excel
          </a>
        </div>
      </div>

      {error && <div className="banner red">{error}</div>}

      {stats.follow_ups_due > 0 && (
        <div className="banner amber">
          <span>⏰</span>
          <div>
            <strong>
              Пора вернуться к клиентам: {stats.follow_ups_due}
            </strong>
            Срок, который поставил менеджер, уже наступил.
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <div className="row" style={{ gap: 20 }}>
            <Stat label="всего" value={stats.total} />
            <Stat label="🔥 горячих" value={stats.by_temperature.hot ?? 0} />
            <Stat label="🙂 тёплых" value={stats.by_temperature.warm ?? 0} />
            <Stat label="❄️ холодных" value={stats.by_temperature.cold ?? 0} />
            {catalog?.categories.map((c) => (
              <Stat
                key={c.key}
                label={`категория ${c.label}`}
                value={stats.by_category[c.key] ?? 0}
              />
            ))}
          </div>

          {Object.keys(stats.by_interest).length > 0 && (
            <div className="row" style={{ marginTop: 16 }}>
              <span className="muted small">Интерес:</span>
              {catalog?.interests
                .filter((i) => stats.by_interest[i.key])
                .map((i) => (
                  <span key={i.key} className="badge blue">
                    {i.label} — {stats.by_interest[i.key]}
                  </span>
                ))}
            </div>
          )}

          {stats.by_city.length > 0 && (
            <div className="row" style={{ marginTop: 10 }}>
              <span className="muted small">Города:</span>
              {stats.by_city.slice(0, 8).map((c) => (
                <span key={c.city} className="badge">
                  {c.city} — {c.count}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-head" style={{ gap: 10, justifyContent: "flex-start" }}>
          <input
            placeholder="Поиск по компании, имени, телефону, почте, заметкам"
            value={filters.search ?? ""}
            onChange={(e) => set("search", e.target.value)}
            style={{ maxWidth: 330 }}
          />
          <select value={filters.status ?? ""} onChange={(e) => set("status", e.target.value)}>
            <option value="">Все статусы</option>
            {catalog?.statuses.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          <select
            value={filters.client_type ?? ""}
            onChange={(e) => set("client_type", e.target.value)}
          >
            <option value="">Любой тип</option>
            {catalog?.client_types.map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
          <select value={filters.category ?? ""} onChange={(e) => set("category", e.target.value)}>
            <option value="">Любая категория</option>
            {catalog?.categories.map((c) => (
              <option key={c.key} value={c.key}>Категория {c.label}</option>
            ))}
          </select>
          <select value={filters.interest ?? ""} onChange={(e) => set("interest", e.target.value)}>
            <option value="">Любое направление</option>
            {catalog?.interests.map((i) => (
              <option key={i.key} value={i.key}>{i.label}</option>
            ))}
          </select>
          {stats.events.length > 0 && (
            <select value={filters.event ?? ""} onChange={(e) => set("event", e.target.value)}>
              <option value="">Все выставки</option>
              {stats.events.map((e) => (
                <option key={e} value={e}>{e}</option>
              ))}
            </select>
          )}
          <select value={filters.owner_id ?? ""} onChange={(e) => set("owner_id", e.target.value)}>
            <option value="">Все менеджеры</option>
            {managers.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
          <input
            placeholder="Город"
            value={filters.city ?? ""}
            onChange={(e) => set("city", e.target.value)}
            style={{ maxWidth: 140 }}
          />
          <label className="check-row nowrap" style={{ margin: 0, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={filters.stale === "1"}
              onChange={(e) => set("stale", e.target.checked ? "1" : "")}
            />
            <span>Залежавшиеся</span>
          </label>
          <label
            className="check-row nowrap"
            style={{ margin: 0, alignItems: "center" }}
            title="Неактуальные контакты, убранные в архив"
          >
            <input
              type="checkbox"
              checked={filters.archived === "1"}
              onChange={(e) => set("archived", e.target.checked ? "1" : "")}
            />
            <span>Архив</span>
          </label>
        </div>

        {archivedMatches > 0 && filters.archived !== "1" && (
          <div className="banner blue" style={{ margin: "0 0 12px" }}>
            <span>
              🗄 Ещё {archivedMatches} {archivedMatches === 1 ? "совпадение" : "совпадения"} в
              архиве.{" "}
              <button className="sm" onClick={() => set("archived", "1")}>
                Показать архив
              </button>
            </span>
          </div>
        )}

        {loading && leads.length === 0 ? (
          <div className="empty">
            <span className="spinner" /> Загружаю…
          </div>
        ) : leads.length === 0 ? (
          <div className="empty">
            <h3>{active ? "Под фильтры ничего не подошло" : "Карточек пока нет"}</h3>
            {!active && (
              <p>
                Пришлите боту запись разговора — он расшифрует её, соберёт карточку
                клиента и положит сюда.
              </p>
            )}
          </div>
        ) : (
          <div className="table-wrap leads-wrap">
            <table className="leads-table">
              <thead>
                <tr>
                  <th>Клиент</th>
                  <th>Тип · категория</th>
                  <th>Направления</th>
                  <th>Кто вёл</th>
                  <th>Менеджер</th>
                  <th>Дата и время</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => {
                  const when = fmtDateParts(l.meeting_start);
                  return (
                  <tr key={l.id}>
                    <td
                      className="lead-main"
                      style={{ minWidth: 200 }}
                      title={l.needs || undefined}
                    >
                      <div className="row" style={{ gap: 6 }}>
                        <strong>{l.company || "без названия"}</strong>
                        <span className={`badge ${TEMPERATURE[l.temperature].cls}`}>
                          {TEMPERATURE[l.temperature].label}
                        </span>
                        {l.duplicate_count > 0 && (
                          <span
                            className="badge amber"
                            title="Есть ещё карточки этой компании — возможно, её завёл другой менеджер"
                          >
                            дубль ×{l.duplicate_count + 1}
                          </span>
                        )}
                        {l.stale && (
                          <span
                            className="badge red"
                            title="Ни звонков, ни писем, ни заметок дольше порога"
                          >
                            тишина {l.silent_days} дн.
                          </span>
                        )}
                      </div>
                      <div className="muted small">
                        {[l.contact_name, l.contact_role].filter(Boolean).join(", ") || "—"}
                      </div>
                      {l.contacts && <div className="muted small mono">{l.contacts}</div>}
                      {l.city && <div className="muted small">{l.city}</div>}
                    </td>
                    <td className="small" data-label="Тип">
                      <div className="lead-val">
                        {l.client_type && catalog && (
                          <div>{labelFor(catalog.client_types, l.client_type)}</div>
                        )}
                        {l.category && <span className="badge">{l.category}</span>}
                        {!l.client_type && !l.category && <span className="muted">—</span>}
                      </div>
                    </td>
                    <td data-label="Направления">
                      <div className="lead-val">
                        <div className="row" style={{ gap: 4 }}>
                          {catalog &&
                            l.interests.map((i) => (
                              <span key={i} className="badge blue">
                                {labelFor(catalog.interests, i)}
                              </span>
                            ))}
                          {l.interests.length === 0 && <span className="muted">—</span>}
                        </div>
                      </div>
                    </td>
                    <td className="small" data-label="Кто вёл">
                      <div className="lead-val">
                        {l.recorded_by_name || (
                          <span
                            className="muted"
                            title="Запись пришла не из телеграма, сотрудник не определился"
                          >
                            —
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="small" data-label="Менеджер">
                      <div className="lead-val">
                        {l.owner_name || <span className="muted">—</span>}
                      </div>
                    </td>
                    <td className="small" data-label="Дата и время">
                      <div className="lead-val">
                        <span className="nowrap">
                          {when.day} {when.month}, {when.time}
                        </span>
                        {l.event_name && <div className="muted">{l.event_name}</div>}
                      </div>
                    </td>
                    <td className="lead-actions">
                      <div className="row nowrap" style={{ justifyContent: "flex-end" }}>
                        <button className="sm ghost" onClick={() => setEditing(l)}>
                          Править
                        </button>
                        <Link className="btn sm" to={`/meetings/${l.meeting_id}`}>
                          Открыть
                        </Link>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <LeadEditModal
          lead={editing}
          catalog={catalog}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div style={{ fontSize: 26, fontWeight: 500, letterSpacing: "-0.03em" }}>
        {value}
      </div>
      <div className="muted small">{label}</div>
    </div>
  );
}
